import React, { useState, useEffect } from "react";
import { Table, Space, Button, Input, Modal, Tooltip, Pagination, Card, Statistic, Switch, Skeleton } from "antd";
import { EyeOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { GoArrowSwitch } from "react-icons/go";
import { customerService } from "../../../services";
import { checkPermission } from "../../../utils/permissions";
import { useDebounce } from "../../../hooks/useDebounce";
import { changeDocumentTitle } from "../../../utils/title";
import { MdOutlineGroups } from "react-icons/md";
import { GoPeople } from "react-icons/go";
import { SlGlobe } from "react-icons/sl";
import { HiOutlineGlobeAlt } from "react-icons/hi";
import { FiFileText } from "react-icons/fi";
import dayjs from "dayjs";
import { withDefaultRender } from "../../../utils/tableDefaultRender";

function Customer() {
  const iconStyle = {
    fontSize: '42px',
    color: 'white',
    padding: '10px',
    borderRadius: '10px',
  };


  const editCustomer = (id) => navigate(`/operations/customer/form/${id}`);
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalshow, setIsModalShow] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDeleteRecord, setSelectedDeleteRecord] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState(null)
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const debouncedSearch = useDebounce(search, 500);

  const columns = withDefaultRender([
    {
      title: "Uid",
      dataIndex: "id",
      key: "id",
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (text) => text.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      align: "center",
    },
    {
      title: "Country",
      dataIndex: "country_name",
      key: "country_name",
      align: "center",
      render: (text) => text.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (text) =>
        text && dayjs(text).isValid()
          ? dayjs(text).format("DD-MMM-YYYY")
          : "-"
    },
    {
      title: "Agreement",
      dataIndex: "document",
      key: "document",
      align: "center",
      width: 120,
      render: (_, record) => (
        <div style={{ padding: "5px" }}>
          {record.isContractSign === 1 ? (
            <Link
              to={`/customers-portal/term-&-condition/${record.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <p
                style={{
                  backgroundColor: "#DBEAFE",
                  padding: "5px",
                  borderRadius: "12px",
                  color: "#1447E6",
                }}
              >
                Signed
              </p>
            </Link>
          ) : (
            <p
              style={{
                backgroundColor: "#E5E7EB",
                padding: "5px",
                borderRadius: "12px",
              }}
            >Not Signed</p>
          )}
        </div >
      ),
    },

    {
      title: "Status",
      key: "isActive",
      align: "center",
      render: (_, record) => (
        <Switch
          checked={record.isActive === 1}
          onChange={() => showModalHandler(record)}
        />
      ),
    },
    {
      title: "Action",
      key: "Action",
      align: "center",
      render: (text, record) => (
        <Space size="small">
          {checkPermission('EDIT_CUSTOMER') && <Tooltip title='Edit'>
            <EditOutlined onClick={() => editCustomer(record.id)} />
          </Tooltip>}

          {checkPermission('VIEW_CUSTOMERS') && <Tooltip title='View'>
            <EyeOutlined onClick={() => navigate("/operations/customer/view/" + record.id)}
            />
          </Tooltip>}

          {checkPermission("DELETE_CUSTOMER") && <Tooltip title='Delete'>
            {record.is_globex_customer === 1 ? null :
              < DeleteOutlined onClick={() => showModal(record)} />
            }
          </Tooltip>}

        </Space>
      ),
    },
  ]);

  const showModalHandler = (record) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const showModal = (record) => {
    setSelectedDeleteRecord(record);
    setIsModalShow(true);
  };

  const handleCancel = () => {
    setIsModalShow(false);
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const initialize = async (page = 1, pageSize = pagination.pageSize) => {
    setLoading(true);
    const { success, data } = await customerService.fetch(page, pageSize, debouncedSearch);
    setLoading(false);
    if (success) {
      setCustomers(data.customers.data);
      setFilteredCustomers(data.customers.data);
      setStatistics(data.statistics)
      setPagination((prev) => ({
        ...prev,
        total: data.customers.total,
        current: page,
        pageSize,
      }));
    }
  };

  const handleDelete = async () => {
    setLoading(true)
    if (!selectedDeleteRecord || !selectedDeleteRecord.id) {
      console.error("No customer selected for deletion.");
      return;
    }
    const response = await customerService.deleteCustomer(
      selectedDeleteRecord.id
    );
    setLoading(false)

    if (response.success) {
      setCustomers((prevData) =>
        prevData.filter((item) => item.id !== selectedDeleteRecord.id)
      );
      initialize();
    } else {
      console.error("Failed to delete the customer.");
    }
    setIsModalShow(false);
  };

  const handleOk = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const response = await customerService.fetchCustomerStatus(
      selectedRecord.id
    );
    if (response.success) {
      setCustomers((prevData) =>
        prevData.map((item) =>
          item.id === selectedRecord.id
            ? { ...item, isActive: item.isActive === 1 ? 0 : 1 }
            : item
        )
      );
      initialize(pagination.current, pagination.pageSize);
      setLoading(false);
      setIsModalVisible(false);
      setSelectedRecord(null);
    }
  };

  useEffect(() => {
    changeDocumentTitle("Customers");
    initialize(pagination.current, pagination.pageSize);
  }, [debouncedSearch]);

  const renderStatusModal = (
    <>
      <Modal
        title={<div style={{ textAlign: "center" }}>Confirm</div>}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        centered
        styles={{
          body: {
            padding: "20px", // Adjust padding for smaller size
          },
        }}
        width={300}
        footer={
          <div className="flex justify-center gap-3">
            <Button
              type="default"
              onClick={handleCancel}
              style={{ width: "30%" }}
            >
              No
            </Button>
            <Button type="primary" onClick={handleOk} style={{ width: "30%" }}>
              Yes
            </Button>
          </div>
        }
      >
        <div style={{ textAlign: "center" }}>Do you want to change status?</div>
      </Modal>
    </>
  );

  const renderDeleteModal = (
    <>
      <Modal
        title={<div style={{ textAlign: "center" }}>Confirm</div>}
        open={isModalshow}
        onOk={handleDelete}
        onCancel={handleCancel}
        centered
        styles={{ body: { padding: "20px" } }}
        width={300}
        footer={
          <div className="flex justify-center gap-3">
            <Button
              type="default"
              onClick={handleCancel}
              style={{ width: "30%" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleDelete}
              style={{ width: "30%" }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <p style={{ textAlign: "center" }}>
          Are you sure you want to delete this?
        </p>
      </Modal>
    </>
  );

  const cardData = [
    {
      title: "TOTAL CUSTOMERS",
      value: statistics.total_customers,
      icon: GoPeople,
      stats: [
        { label: "Active", value: statistics.active, bg: "#F0FDF4", color: "#008236" },
        { label: "Inactive", value: statistics.inactive, bg: "#FEF2F2", color: "#C10007" },
        { label: "New (Month)", value: statistics.new_this_month, bg: "#EEF2FF", color: "#432DD7" }
      ]
    },
    {
      title: "AGREEMENT",
      value: statistics.agreements_total,
      icon: FiFileText,
      stats: [
        { label: "Signed", value: statistics.signed, bg: "#EEF2FF", color: "#432DD7" },
        { label: "Not Signed", value: statistics.not_signed, bg: "#F3F4F6", color: "#000" }
      ]
    },
    {
      title: "COUNTRIES",
      value: statistics.countries_total,
      icon: HiOutlineGlobeAlt,
      countries:
        Object.values(statistics?.countries || []).slice(0, 5).map((item) => item.country).join(", ")
    }
  ];

  return (
    <>
      <div className="w-full flex gap-3 mb-4">
        {cardData.map((card, index) => {
          const Icon = card.icon;

          return (

            <Card key={index} className="!w-full !rounded-3xl border-[#CCCCCC]">
              {loading ? (
                <Skeleton active paragraph={{ rows: 3 }} />)
                : (
                  <div>
                    <div className="flex justify-between mb-1">
                      <Statistic title={card.title} value={card.value} />
                      <Icon className="bg-primary" style={{ ...iconStyle }} />
                    </div>

                    {card.stats && (
                      <div className="flex gap-3">
                        {card.stats.map((stat, i) => (
                          <div
                            key={i}
                            className="flex flex-col p-3 w-full rounded-lg"
                            style={{ background: stat.bg }}
                          >
                            <span className="text-[11px] text-[#6A7282] py-1">
                              {stat.label}
                            </span>
                            <span
                              className="text-base font-medium"
                              style={{ color: stat.color }}
                            >
                              {stat.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}


                    {card.countries && (
                      <div className="relative group">

                        {/* Main Card */}
                        <div className="flex flex-col bg-[#F9FAFB] p-3 w-full rounded-lg cursor-pointer hover:bg-[#F3F4F6] transition">
                          <span className="text-[11px] text-[#6A7282] py-1">
                            Customer Countries
                          </span>
                          <span className="text-base truncate">
                            {card.countries}
                          </span>
                        </div>

                        {/* Hover Dropdown */}
                        <div className="absolute left-0 top-full mt-2 w-full bg-white border rounded-xl shadow-lg p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">

                          <div className="max-h-[200px] overflow-y-auto pr-1">
                            {(statistics?.countries || []).map((item, i) => (
                              <div
                                key={i}
                                className="flex justify-between text-xs py-1 px-2 hover:bg-gray-100 rounded-md transition"
                              >
                                <span>{item.country}</span>
                                <span className="font-semibold text-primary">{item.count}</span>
                              </div>
                            ))}
                          </div>

                        </div>
                      </div>
                    )}

                  </div>
                )}
            </Card>
          );
        })}
      </div>

      <div style={{ padding: "5px", border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#FFFFFF" }}>

        <div className="flex justify-between items-center">
          <h1 style={{ fontWeight: "bold" }} className="pl-2 pt-3">List of Customer</h1>

          <Input
            placeholder="Search by Name & Email"
            prefix={<SearchOutlined />}
            style={{
              width: "25%",
              borderRadius: "20px",
              fontSize: 14,
              margin: 10
            }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredCustomers}
          loading={loading}
          // pagination={{
          //   current: pagination.current,
          //   total: pagination.total,
          //   pageSize: pagination.pageSize,
          //   onChange: (page) => initialize(page, pagination.pageSize),
          //   showSizeChanger: false,
          // }}
          pagination={false}
          style={{ marginTop: "10px" }}
          footer={() => (
            <div className="flex justify-between items-center text-xs ">
              <div className=" text-gray-600">
                Showing {(pagination.current - 1) * pagination.pageSize + 1} to{" "}
                {Math.min(pagination.current * pagination.pageSize, pagination.total)} of{" "}
                {pagination.total} entries
              </div>
              <Pagination
                current={pagination.current}
                total={pagination.total}
                pageSize={pagination.pageSize}
                showSizeChanger={false}
                onChange={(page) => initialize(page, pagination.pageSize, search)}
                itemRender={(current, type, originalElement) => {
                  if (type === "prev") return <a className="bg-[#EEEEEE] p-2 rounded-sm hover:text-primary">Previous</a>;
                  if (type === "next") return <a className="bg-[#EEEEEE] p-2 rounded-sm hover:text-primary">Next</a>;
                  return originalElement;
                }}
              />
            </div>
          )}
        />

        {renderStatusModal}

        {renderDeleteModal}
      </div>
    </>

  );
}

export default Customer;
